import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User } from 'lucide-react';
import { useProfile, useUpdateProfile } from '@/hooks/useProfile';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { EmailUpdateForm } from '@/components/email-update-flow/EmailUpdateForm';

const COUNTRIES = [
  'Kenya', 'Nigeria', 'South Africa', 'Ghana', 'Tanzania', 'Uganda',
  'United States', 'United Kingdom', 'Canada', 'Germany', 'France', 'India',
  'Brazil', 'Australia', 'Japan', 'Other',
];

export function PersonalDetailsSettings() {
  const { data: profile, isLoading } = useProfile();
  const updateProfile = useUpdateProfile();

  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [country, setCountry] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name ?? '');
      setMiddleName(profile.middle_name ?? '');
      setLastName(profile.last_name ?? '');
      setCountry(profile.country ?? '');
      setDateOfBirth(profile.date_of_birth ?? '');
      setPhoneNumber(profile.phone_number ?? '');
    }
  }, [profile]);

  const handleSave = () => {
    updateProfile.mutate(
      {
        first_name: firstName || null,
        middle_name: middleName || null,
        last_name: lastName || null,
        country: country || null,
        date_of_birth: dateOfBirth || null,
        phone_number: phoneNumber || null,
      },
      {
        onSuccess: () => toast.success('Personal details updated'),
        onError: () => toast.error('Failed to update personal details'),
      }
    );
  };

  if (isLoading) {
    return <Skeleton className="h-96 w-full" />;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Personal Information
          </CardTitle>
          <CardDescription>Update your personal details.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="first-name">First Name</Label>
              <Input id="first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="middle-name">Middle Name</Label>
              <Input id="middle-name" value={middleName} onChange={(e) => setMiddleName(e.target.value)} placeholder="Middle name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last-name">Last Name</Label>
              <Input id="last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <select
                id="country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">Select country</option>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="dob">Date of Birth</Label>
              <Input id="dob" type="date" value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} />
            </div>
          </div>

          <Button onClick={handleSave} disabled={updateProfile.isPending}>
            {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
          <CardDescription>Your email and phone number.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <EmailUpdateForm />
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number (optional)</Label>
            <Input
              id="phone"
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+254 700 000 000"
            />
          </div>
          <Button
            onClick={() => {
              updateProfile.mutate({ phone_number: phoneNumber || null }, {
                onSuccess: () => toast.success('Phone number updated'),
                onError: () => toast.error('Failed to update phone number'),
              });
            }}
            disabled={updateProfile.isPending}
            variant="outline"
          >
            Update Contact
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
